/**
 * Formulario de visita reutilizable.
 * Usado en ClienteDetalle (entidad_id conocida) y VisitasGlobal (showEmpresaSearch=true).
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { contactosApi, usuariosApi, iaApi, entidadesApi } from '../../services/api';
import { Campo, Select } from '../cliente/_shared';
import AiProposal from '../AiProposal';
import { Search, X, UserPlus, Loader2, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_ARRAY = [];

const EMPTY_FORM = () => ({
  fecha: new Date().toISOString().slice(0, 10),
  tipo: 'comercial',
  contactos_ids: [],
  asistentes_internos_ids: [],
  resumen: '',
  siguientes_pasos: '',
  resultado: '',
  proxima_accion_fecha: '',
  menciones_contactos_ids: [],
});

export function VisitaFormDialog({
  open,
  onOpenChange,
  onSave,               // async (payload) => void — parent hace la llamada a la API
  initialData = null,   // visita para edición, null = nueva
  entidad_id = null,    // empresa ya conocida (ClienteDetalle)
  entidad_nombre = null,
  contactos_empresa = EMPTY_ARRAY,
  showEmpresaSearch = false,  // VisitasGlobal: permite buscar empresa dentro del formulario
  onTasksCreated = null,      // callback tras ejecutar propuesta IA de tareas
}) {
  const [form, setForm] = useState(EMPTY_FORM());
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  // Empresa (solo cuando showEmpresaSearch=true)
  const [empresaQ, setEmpresaQ] = useState('');
  const [empresaSugs, setEmpresaSugs] = useState([]);
  const [empresaSel, setEmpresaSel] = useState(null);
  const efectivoEntidadId = entidad_id || empresaSel?.id || null;

  // Contactos
  const [localContactos, setLocalContactos] = useState(contactos_empresa);
  const [asistenteBusqueda, setAsistenteBusqueda] = useState('');
  const [asistenteSugs, setAsistenteSugs] = useState([]);
  const [asistenteLoading, setAsistenteLoading] = useState(false);
  const [cache, setCache] = useState({});

  // Nuevo contacto rápido
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({ nombre: '', apellidos: '', cargo: '', email_trabajo: '' });
  const [quickErr, setQuickErr] = useState('');

  // Equipo interno
  const [usuariosLista, setUsuariosLista] = useState([]);

  // IA
  const [iaLoading, setIaLoading] = useState(false);
  const [iaMenuOpen, setIaMenuOpen] = useState(false);
  const [iaEstiloOpen, setIaEstiloOpen] = useState(false);
  const [iaIdiomaOpen, setIaIdiomaOpen] = useState(false);
  const [iaDiff, setIaDiff] = useState(null);
  const [propuestaIA, setPropuestaIA] = useState(null);

  // Menciones @contacto (disparadas con #)
  const [mencionQ, setMencionQ] = useState(null);
  const [mencionSugs, setMencionSugs] = useState([]);
  const [mencionLoading, setMencionLoading] = useState(false);
  const resumenRef = useRef(null);

  // ── Inicializar formulario al abrir ──────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setForm(initialData ? {
      fecha: initialData.fecha || new Date().toISOString().slice(0, 10),
      tipo: initialData.tipo || 'comercial',
      contactos_ids: initialData.contactos_ids || [],
      asistentes_internos_ids: initialData.asistentes_internos_ids || [],
      resumen: initialData.resumen || '',
      siguientes_pasos: initialData.siguientes_pasos || '',
      resultado: initialData.resultado || '',
      proxima_accion_fecha: initialData.proxima_accion_fecha || '',
      menciones_contactos_ids: initialData.menciones_contactos_ids || [],
    } : EMPTY_FORM());
    setErr(''); setPropuestaIA(null);
    setIaMenuOpen(false); setAsistenteBusqueda(''); setAsistenteSugs([]);
    setMencionQ(null); setMencionSugs([]);
    if (!entidad_id) { setEmpresaSel(null); setEmpresaQ(''); }
  }, [open, initialData, entidad_id]);

  // Cargar usuarios seleccionables al montar
  useEffect(() => {
    usuariosApi.seleccionables().then(setUsuariosLista).catch(() => {});
  }, []);

  // Contactos de la empresa efectiva
  useEffect(() => {
    if (entidad_id) { setLocalContactos(contactos_empresa); return; }
    if (!efectivoEntidadId) { setLocalContactos([]); return; }
    contactosApi.list({ entidad_id: efectivoEntidadId })
      .then(setLocalContactos).catch(() => {});
  }, [efectivoEntidadId, entidad_id, contactos_empresa]);

  // Autocomplete empresa
  useEffect(() => {
    if (!showEmpresaSearch || !open) return;
    const q = empresaQ.trim();
    if (q.length < 2) { setEmpresaSugs([]); return; }
    const t = setTimeout(async () => {
      try { const r = await entidadesApi.list({ q, limit: 8 }); setEmpresaSugs(Array.isArray(r) ? r : []); }
      catch { setEmpresaSugs([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [empresaQ, showEmpresaSearch, open]);

  // Autocomplete asistentes
  useEffect(() => {
    if (!open) { setAsistenteSugs([]); return; }
    const q = asistenteBusqueda.trim();
    if (q.length < 2) { setAsistenteSugs([]); return; }
    const t = setTimeout(async () => {
      setAsistenteLoading(true);
      try { const r = await contactosApi.list({ q, limit: 8 }); setAsistenteSugs(r || []); }
      catch { setAsistenteSugs([]); }
      finally { setAsistenteLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [asistenteBusqueda, open]);

  // Autocomplete menciones (#query → @Nombre en resumen)
  useEffect(() => {
    if (mencionQ === null) { setMencionSugs([]); return; }
    const t = setTimeout(async () => {
      setMencionLoading(true);
      try { const r = await contactosApi.list({ q: mencionQ, limit: 6 }); setMencionSugs(r || []); }
      catch { setMencionSugs([]); }
      finally { setMencionLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [mencionQ]);

  // Precarga cache de contactos ya seleccionados (para chips)
  useEffect(() => {
    if (!open) return;
    const faltantes = (form.contactos_ids || []).filter((cid) => !cache[cid]);
    if (!faltantes.length) return;
    const nuevo = { ...cache };
    faltantes.forEach((cid) => { const c = localContactos.find((x) => x.id === cid); if (c) nuevo[cid] = c; });
    const pendientes = faltantes.filter((cid) => !nuevo[cid]);
    if (pendientes.length) {
      (async () => {
        for (const cid of pendientes) { try { nuevo[cid] = await contactosApi.get(cid); } catch {} }
        setCache(nuevo);
      })();
    } else setCache(nuevo);
    // eslint-disable-next-line
  }, [form.contactos_ids, open, localContactos]);

  const toggleAsistente = (c) => {
    const set = new Set(form.contactos_ids || []);
    if (set.has(c.id)) set.delete(c.id); else set.add(c.id);
    setForm((f) => ({ ...f, contactos_ids: [...set] }));
    setCache((prev) => ({ ...prev, [c.id]: c }));
    setAsistenteBusqueda(''); setAsistenteSugs([]);
  };

  const handleResumenChange = (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, resumen: val }));
    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const match = before.match(/#(\S*)$/);
    setMencionQ(match ? match[1] : null);
    if (!match) setMencionSugs([]);
  };

  const handleResumenKeyDown = (e) => {
    if (e.key === 'Escape' && mencionQ !== null) {
      setMencionQ(null); setMencionSugs([]); e.preventDefault();
    }
  };

  const seleccionarMencion = (c) => {
    const label = `${c.nombre || ''} ${c.apellidos || ''}`.trim();
    const el = resumenRef.current;
    if (!el) return;
    const val = form.resumen;
    const cursor = el.selectionStart;
    const before = val.slice(0, cursor);
    const after = val.slice(cursor);
    const match = before.match(/#(\S*)$/);
    if (!match) return;
    const prefix = before.slice(0, before.length - match[0].length);
    const nuevoVal = `${prefix}@${label}${after}`;
    const newCursor = prefix.length + label.length + 1;
    setForm((f) => ({
      ...f,
      resumen: nuevoVal,
      menciones_contactos_ids: [...new Set([...(f.menciones_contactos_ids || []), c.id])],
    }));
    setMencionQ(null); setMencionSugs([]);
    setTimeout(() => { el.focus(); el.setSelectionRange(newCursor, newCursor); }, 0);
  };

  const crearContactoRapido = async (ev) => {
    ev?.preventDefault?.();
    setQuickErr('');
    if (!quickForm.nombre.trim()) { setQuickErr('El nombre es obligatorio'); return; }
    try {
      const nuevo = await contactosApi.create({
        nombre: quickForm.nombre.trim(),
        apellidos: quickForm.apellidos.trim() || null,
        cargo: quickForm.cargo.trim() || null,
        email_trabajo: quickForm.email_trabajo.trim() || null,
        entidad_id: efectivoEntidadId,
        nivel_relacion: 'sin_definir',
      });
      setForm((f) => ({ ...f, contactos_ids: [...(f.contactos_ids || []), nuevo.id] }));
      setCache((prev) => ({ ...prev, [nuevo.id]: nuevo }));
      if (efectivoEntidadId) {
        contactosApi.list({ entidad_id: efectivoEntidadId }).then(setLocalContactos).catch(() => {});
      }
      setQuickOpen(false);
      setQuickForm({ nombre: '', apellidos: '', cargo: '', email_trabajo: '' });
      toast.success('Contacto creado y añadido a la visita');
    } catch (e) { setQuickErr(e.message); }
  };

  const iaOperar = async (operacion, extra = {}) => {
    const texto = form.resumen;
    if (!texto || texto.length < 3) { toast.info('Escribe el resumen primero'); return; }
    setIaMenuOpen(false); setIaEstiloOpen(false); setIaIdiomaOpen(false);
    setIaLoading(true);
    try {
      if (operacion === 'corregir') {
        const r = await iaApi.corregirTexto(texto);
        setIaDiff({ ...r, tipo: 'corregir', label: 'Corrección ortográfica' });
      } else if (operacion === 'reescribir') {
        const r = await iaApi.reescribir(texto, extra.estilo || 'profesional');
        setIaDiff({ original: r.original, corregido: r.reescrito, diff: null, label: `Reescrito (${extra.estilo})` });
      } else if (operacion === 'traducir') {
        const r = await iaApi.traducir(texto, extra.idioma);
        setIaDiff({ original: r.original, corregido: r.traducido, diff: null, label: `Traducido a ${extra.idioma.toUpperCase()}` });
      } else if (operacion === 'tareas') {
        const r = await iaApi.analizarVisita({ resumen: texto, entidad_id: efectivoEntidadId, contactos_ids: form.contactos_ids || [] });
        if (r.acciones_sugeridas?.length) setPropuestaIA(r.acciones_sugeridas);
        else toast.info('La IA no sugiere tareas nuevas');
      }
    } catch (e) { toast.error(e.message); }
    finally { setIaLoading(false); }
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault?.();
    setErr('');
    if (!form.fecha) { setErr('La fecha es obligatoria'); return; }
    if (!form.resumen?.trim() || form.resumen.trim().length < 2) {
      setErr('El resumen es obligatorio (mín. 2 caracteres)'); return;
    }
    setSaving(true);
    try {
      await onSave({
        fecha: form.fecha,
        tipo: form.tipo,
        contactos_ids: form.contactos_ids || [],
        asistentes_internos_ids: form.asistentes_internos_ids || [],
        resumen: form.resumen.trim(),
        siguientes_pasos: form.siguientes_pasos || null,
        resultado: form.resultado || null,
        proxima_accion_fecha: form.proxima_accion_fecha || null,
        entidad_id: efectivoEntidadId || null,
        menciones_contactos_ids: form.menciones_contactos_ids || [],
      });
      onOpenChange(false);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      {/* ── Formulario principal ─────────────────────────────────────────── */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl p-0 overflow-visible" data-testid="dialog-visita">
          <form onSubmit={handleSubmit}>
            <DialogHeader className="px-6 pt-6">
              <DialogTitle>{initialData ? 'Editar visita' : 'Nueva visita'}</DialogTitle>
              <DialogDescription>
                {entidad_nombre
                  ? <>Registro de reunión con personas de <span className="font-semibold">{entidad_nombre}</span>.</>
                  : <>Registra una visita o reunión. La empresa es <span className="font-semibold">opcional</span>.</>}
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[80vh] overflow-y-auto px-6 pb-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">

              {/* Empresa opcional (VisitasGlobal) */}
              {showEmpresaSearch && (
                <Campo label="Empresa (opcional)" wide>
                  {empresaSel ? (
                    <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-brand-200 bg-brand-50">
                      <span className="text-sm font-medium text-brand-800">
                        {empresaSel.nombre_comercial || empresaSel.razon_social}
                        {empresaSel.provincia && <span className="ml-2 text-xs text-brand-600">· {empresaSel.provincia}</span>}
                      </span>
                      <button type="button" onClick={() => { setEmpresaSel(null); setEmpresaQ(''); }} className="text-brand-700 hover:text-rose-600" aria-label="Quitar empresa">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                      <Input placeholder="Buscar empresa por razón social, CIF…" value={empresaQ} onChange={(e) => setEmpresaQ(e.target.value)} className="pl-9" />
                      {empresaSugs.length > 0 && (
                        <div className="absolute left-0 right-0 mt-1 bg-white border border-ink-200 rounded-md shadow-lg z-[9999] max-h-56 overflow-auto">
                          {empresaSugs.map((e) => (
                            <button key={e.id} type="button" onClick={() => { setEmpresaSel(e); setEmpresaQ(''); setEmpresaSugs([]); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-ink-50 border-b border-ink-100 last:border-0">
                              <div className="font-medium text-ink-900">{e.nombre_comercial || e.razon_social}</div>
                              <div className="text-xs text-ink-500">{e.cif && <>CIF {e.cif} · </>}{e.provincia || '—'}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Campo>
              )}

              <Campo label="Fecha *" required>
                <Input type="date" value={form.fecha} onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} data-testid="vf-fecha" />
              </Campo>
              <Campo label="Tipo">
                <Select value={form.tipo} onChange={(v) => setForm((f) => ({ ...f, tipo: v }))} options={[
                  { v: 'comercial', l: 'Comercial' }, { v: 'tecnica', l: 'Técnica' },
                  { v: 'cortesia', l: 'Cortesía' }, { v: 'seguimiento', l: 'Seguimiento' }, { v: 'otra', l: 'Otra' },
                ]} />
              </Campo>
              <Campo label="Resultado">
                <Select value={form.resultado} onChange={(v) => setForm((f) => ({ ...f, resultado: v }))} options={[
                  { v: '', l: '— Sin especificar —' }, { v: 'positivo', l: 'Positivo' },
                  { v: 'neutro', l: 'Neutro' }, { v: 'negativo', l: 'Negativo' },
                ]} />
              </Campo>
              <Campo label="Próxima acción (fecha)">
                <Input type="date" value={form.proxima_accion_fecha || ''} onChange={(e) => setForm((f) => ({ ...f, proxima_accion_fecha: e.target.value }))} />
              </Campo>

              {/* Asistentes externos (contactos) */}
              <Campo label="Asistentes (contactos)" wide>
                {(form.contactos_ids || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="vf-asistentes-chips">
                    {form.contactos_ids.map((cid) => {
                      const c = cache[cid] || localContactos.find((x) => x.id === cid);
                      const label = c
                        ? `${c.nombre || ''} ${c.apellidos || ''}`.trim() + (c.entidad_nombre && c.entidad_id !== efectivoEntidadId ? ` · ${c.entidad_nombre}` : '')
                        : cid;
                      return (
                        <span key={cid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-50 border border-brand-200 text-brand-700 text-xs" data-testid={`vf-asistente-chip-${cid}`}>
                          {label}
                          <button type="button" onClick={() => setForm((f) => ({ ...f, contactos_ids: (f.contactos_ids || []).filter((x) => x !== cid) }))} className="hover:text-red-600" aria-label="Quitar asistente">×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="relative">
                  <div className="flex gap-2">
                    <Input placeholder="Buscar contacto por nombre, apellidos, cargo o email…" value={asistenteBusqueda}
                      onChange={(e) => setAsistenteBusqueda(e.target.value)} data-testid="vf-asistente-search" />
                    {efectivoEntidadId && (
                      <Button type="button" variant="outline" className="shrink-0" data-testid="btn-nuevo-contacto-rapido"
                        onClick={() => {
                          const partes = asistenteBusqueda.trim().split(/\s+/);
                          setQuickForm({ nombre: partes[0] || '', apellidos: partes.slice(1).join(' ') || '', cargo: '', email_trabajo: '' });
                          setQuickErr(''); setQuickOpen(true);
                        }}>
                        <UserPlus className="w-4 h-4 mr-1" /> Nuevo
                      </Button>
                    )}
                  </div>
                  {asistenteBusqueda.trim().length >= 2 && (
                    <div className="absolute left-0 right-0 mt-1 bg-white border border-ink-200 rounded-md shadow-lg z-[9999] max-h-64 overflow-auto" data-testid="vf-asistente-sugerencias">
                      {asistenteLoading && <div className="p-2 text-xs text-ink-400">Buscando…</div>}
                      {!asistenteLoading && asistenteSugs.length === 0 && (
                        <div className="p-2 text-xs text-ink-400">Sin resultados.{efectivoEntidadId ? ' Pulsa "Nuevo" para crearlo.' : ''}</div>
                      )}
                      {asistenteSugs.map((c) => {
                        const ya = (form.contactos_ids || []).includes(c.id);
                        return (
                          <button key={c.id} type="button" disabled={ya} onClick={() => toggleAsistente(c)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-ink-50 border-b border-ink-100 last:border-0 ${ya ? 'opacity-50 cursor-not-allowed' : ''}`}
                            data-testid={`vf-asistente-sug-${c.id}`}>
                            <div className="font-medium text-ink-900">{c.nombre} {c.apellidos || ''}{ya && <span className="text-[10px] text-emerald-600 ml-2">· ya añadido</span>}</div>
                            <div className="text-xs text-ink-500">{c.cargo && <>{c.cargo} · </>}{c.entidad_nombre || '—'}{c.email_trabajo && <> · {c.email_trabajo}</>}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {localContactos.length > 0 && (
                  <details className="mt-2 text-xs text-ink-500">
                    <summary className="cursor-pointer hover:text-ink-700">Ver contactos de esta empresa ({localContactos.length})</summary>
                    <div className="mt-1 border border-ink-200 rounded-md p-2 max-h-32 overflow-y-auto bg-white">
                      {localContactos.map((c) => (
                        <label key={c.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer hover:bg-ink-50 px-1 rounded">
                          <input type="checkbox" className="accent-brand-500"
                            checked={(form.contactos_ids || []).includes(c.id)}
                            onChange={(e) => {
                              const set = new Set(form.contactos_ids);
                              if (e.target.checked) set.add(c.id); else set.delete(c.id);
                              setForm((f) => ({ ...f, contactos_ids: [...set] }));
                              setCache((prev) => ({ ...prev, [c.id]: c }));
                            }} data-testid={`vf-asistente-${c.id}`} />
                          <span className="text-ink-700">{c.nombre} {c.apellidos || ''}{c.cargo && <span className="text-ink-400"> · {c.cargo}</span>}</span>
                        </label>
                      ))}
                    </div>
                  </details>
                )}
              </Campo>

              {/* Asistentes internos */}
              <Campo label="Nuestros asistentes (equipo interno)" wide>
                <div className="border border-ink-200 rounded-md p-2 max-h-40 overflow-y-auto bg-white">
                  {usuariosLista.length === 0
                    ? <p className="text-xs text-ink-400">Cargando equipo...</p>
                    : usuariosLista.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer hover:bg-ink-50 px-1 rounded">
                        <input type="checkbox" className="accent-brand-500"
                          checked={(form.asistentes_internos_ids || []).includes(u.id)}
                          onChange={(e) => {
                            const set = new Set(form.asistentes_internos_ids || []);
                            if (e.target.checked) set.add(u.id); else set.delete(u.id);
                            setForm((f) => ({ ...f, asistentes_internos_ids: [...set] }));
                          }} data-testid={`vf-interno-${u.id}`} />
                        <span>{u.nombre_completo} <span className="text-ink-400 text-xs">· {u.rol}</span></span>
                      </label>
                    ))
                  }
                </div>
              </Campo>

              {/* Resumen + IA */}
              <Campo label="Resumen de la visita *" required wide>
                <div className="relative">
                  <div className="absolute right-2 top-2 z-10">
                    <div className="relative">
                      <button type="button" onClick={() => setIaMenuOpen(!iaMenuOpen)}
                        disabled={iaLoading || !form.resumen || form.resumen.length < 3}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-[#7B5EA7] text-white hover:bg-[#9B59B6] disabled:opacity-40"
                        data-testid="btn-ia-menu" title="Acciones de IA sobre el resumen">
                        {iaLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        IA
                      </button>
                      {iaMenuOpen && (
                        <div className="absolute right-0 top-8 w-64 bg-white rounded-md shadow-xl border border-ink-200 z-[9999] py-1 text-sm" data-testid="ia-menu">
                          <button type="button" onClick={() => iaOperar('corregir')} className="w-full px-3 py-2 text-left hover:bg-ink-50 flex items-center gap-2" data-testid="ia-op-corregir">
                            <span>✍️</span> Corregir ortografía y gramática
                          </button>
                          <div className="relative">
                            <button type="button" onClick={() => { setIaEstiloOpen(!iaEstiloOpen); setIaIdiomaOpen(false); }} className="w-full px-3 py-2 text-left hover:bg-ink-50 flex items-center justify-between" data-testid="ia-op-reescribir">
                              <span><span className="mr-2">🔄</span>Reescribir</span><span className="text-ink-400 text-xs">▸</span>
                            </button>
                            {iaEstiloOpen && (
                              <div className="absolute right-full top-0 mr-1 w-44 bg-white rounded-md shadow-xl border border-ink-200 py-1 z-[9999]">
                                {['profesional', 'breve', 'detallado', 'simple', 'formal'].map((e) => (
                                  <button key={e} type="button" onClick={() => iaOperar('reescribir', { estilo: e })} className="w-full px-3 py-1.5 text-left hover:bg-ink-50 text-xs capitalize" data-testid={`ia-estilo-${e}`}>{e}</button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="relative">
                            <button type="button" onClick={() => { setIaIdiomaOpen(!iaIdiomaOpen); setIaEstiloOpen(false); }} className="w-full px-3 py-2 text-left hover:bg-ink-50 flex items-center justify-between" data-testid="ia-op-traducir">
                              <span><span className="mr-2">🌐</span>Traducir</span><span className="text-ink-400 text-xs">▸</span>
                            </button>
                            {iaIdiomaOpen && (
                              <div className="absolute right-full top-0 mr-1 w-36 bg-white rounded-md shadow-xl border border-ink-200 py-1 z-[9999]">
                                {[['en', 'Inglés'], ['fr', 'Francés'], ['pt', 'Portugués'], ['de', 'Alemán'], ['it', 'Italiano'], ['ca', 'Catalán']].map(([v, l]) => (
                                  <button key={v} type="button" onClick={() => iaOperar('traducir', { idioma: v })} className="w-full px-3 py-1.5 text-left hover:bg-ink-50 text-xs" data-testid={`ia-idioma-${v}`}>{l}</button>
                                ))}
                              </div>
                            )}
                          </div>
                          {efectivoEntidadId && (
                            <>
                              <div className="border-t border-ink-100 my-1" />
                              <button type="button" onClick={() => iaOperar('tareas')} className="w-full px-3 py-2 text-left hover:bg-ink-50 flex items-center gap-2" data-testid="ia-op-tareas">
                                <span>✅</span> Proponer y crear tareas
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <Textarea ref={resumenRef} rows={4} value={form.resumen} onChange={handleResumenChange} onKeyDown={handleResumenKeyDown} data-testid="vf-resumen" />
                  {mencionQ !== null && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-ink-200 rounded-md shadow-lg z-[9999] max-h-48 overflow-auto" data-testid="menciones-dropdown">
                      {mencionLoading && <div className="p-2 text-xs text-ink-400">Buscando…</div>}
                      {!mencionLoading && mencionQ.length === 0 && <div className="p-2 text-xs text-ink-400">Escribe para buscar un contacto…</div>}
                      {!mencionLoading && mencionQ.length > 0 && mencionSugs.length === 0 && <div className="p-2 text-xs text-ink-400">Sin resultados</div>}
                      {mencionSugs.map((c) => {
                        const label = `${c.nombre || ''} ${c.apellidos || ''}`.trim();
                        return (
                          <button key={c.id} type="button"
                            onMouseDown={(e) => { e.preventDefault(); seleccionarMencion(c); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-ink-50 border-b border-ink-100 last:border-0"
                            data-testid={`mencion-sug-${c.id}`}>
                            <div className="font-medium text-ink-900">{label}</div>
                            <div className="text-xs text-ink-500">{c.entidad_nombre || '—'}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Campo>

              {propuestaIA && (
                <div className="sm:col-span-2 p-3 rounded-md border border-[#7B5EA7]/30 bg-[#7B5EA7]/5">
                  <AiProposal
                    acciones={propuestaIA}
                    onEjecutar={() => { setPropuestaIA(null); onTasksCreated?.(); }}
                    onCancelar={() => setPropuestaIA(null)}
                  />
                </div>
              )}

              <Campo label="Siguientes pasos" wide>
                <Textarea rows={2} value={form.siguientes_pasos} onChange={(e) => setForm((f) => ({ ...f, siguientes_pasos: e.target.value }))} />
              </Campo>

              {err && <div className="sm:col-span-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}
            </div>
            </div>

            <DialogFooter className="px-6 pb-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" disabled={saving} data-testid="btn-guardar-visita">
                {saving ? 'Guardando…' : 'Guardar visita'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Resultado IA (sub-diálogo) ───────────────────────────────────── */}
      <Dialog open={!!iaDiff} onOpenChange={(o) => { if (!o) setIaDiff(null); }}>
        <DialogContent className="max-w-2xl" data-testid="dialog-ia-resultado">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#7B5EA7]" />
              {iaDiff?.label || 'Resultado IA'}
            </DialogTitle>
            <DialogDescription>Revisa el resultado antes de aplicarlo al resumen de la visita.</DialogDescription>
          </DialogHeader>
          {iaDiff?.diff && Array.isArray(iaDiff.diff) ? (
            <div className="text-sm leading-relaxed p-3 bg-ink-50 rounded border border-ink-200" data-testid="ia-diff-view">
              {iaDiff.diff.map((seg, i) => {
                if (seg.tipo === 'equal') return <span key={i}>{seg.texto} </span>;
                if (seg.tipo === 'del') return <span key={i} className="line-through text-rose-600 bg-rose-50 px-1 rounded">{seg.texto} </span>;
                if (seg.tipo === 'add') return <span key={i} className="text-emerald-700 bg-emerald-50 px-1 rounded font-medium">{seg.texto} </span>;
                return null;
              })}
            </div>
          ) : iaDiff && (
            <div className="space-y-3">
              <div>
                <p className="text-xs text-ink-500 uppercase tracking-wider mb-1">Original</p>
                <div className="p-3 bg-ink-50 rounded border border-ink-200 text-sm whitespace-pre-wrap">{iaDiff.original}</div>
              </div>
              <div>
                <p className="text-xs text-[#7B5EA7] uppercase tracking-wider mb-1">Propuesta IA</p>
                <div className="p-3 bg-[#7B5EA7]/5 rounded border border-[#7B5EA7]/30 text-sm whitespace-pre-wrap" data-testid="ia-resultado-nuevo">{iaDiff.corregido}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIaDiff(null)} data-testid="btn-ia-cancelar">
              <X className="w-3.5 h-3.5 mr-1" /> Cancelar
            </Button>
            <Button type="button" onClick={() => { setForm((f) => ({ ...f, resumen: iaDiff?.corregido || f.resumen })); toast.success('Texto actualizado'); setIaDiff(null); }}
              className="bg-[#7B5EA7] hover:bg-[#9B59B6] text-white" data-testid="btn-ia-aplicar">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Aplicar al resumen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Nuevo contacto rápido (sub-diálogo) ─────────────────────────── */}
      {efectivoEntidadId && (
        <Dialog open={quickOpen} onOpenChange={(o) => { if (!o) setQuickOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="dialog-contacto-quick">
            <form onSubmit={crearContactoRapido}>
              <DialogHeader>
                <DialogTitle>Nuevo contacto</DialogTitle>
                <DialogDescription>Se creará y se añadirá automáticamente a esta visita.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="Nombre *" required>
                    <Input value={quickForm.nombre} onChange={(e) => setQuickForm((f) => ({ ...f, nombre: e.target.value }))} autoFocus data-testid="qc-nombre" />
                  </Campo>
                  <Campo label="Apellidos">
                    <Input value={quickForm.apellidos} onChange={(e) => setQuickForm((f) => ({ ...f, apellidos: e.target.value }))} data-testid="qc-apellidos" />
                  </Campo>
                </div>
                <Campo label="Cargo">
                  <Input value={quickForm.cargo} onChange={(e) => setQuickForm((f) => ({ ...f, cargo: e.target.value }))} data-testid="qc-cargo" />
                </Campo>
                <Campo label="Email profesional">
                  <Input type="email" value={quickForm.email_trabajo} onChange={(e) => setQuickForm((f) => ({ ...f, email_trabajo: e.target.value }))} data-testid="qc-email" />
                </Campo>
                {quickErr && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{quickErr}</div>}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setQuickOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-guardar-contacto-quick">Crear y añadir</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
